import { ExpertType } from '#app/__generated__/api.js';
import { type useBadges_userProfile$key } from '#app/__generated__/relay/useBadges_userProfile.graphql.js';
import { getRateRangeFromMinHourlyRate } from '#app/components/DurationAndRate/utils.js';
import { useMembersOnlyBadge } from '#app/hooks/useMembersOnlyBadge.js';
import { EXPERT_PARTNER_USER_GROUPS } from '@contra/expert-partnerships';
import { graphql, useFragment } from '@contra/relay';

const getBadgeFromCategory = (category: string) => {
  switch (category) {
    case 'AUDIO':
      return 'Music & Audio';
    case 'BUSINESS':
      return 'Business';
    case 'CUSTOMER_SUPPORT':
      return 'Customer Support';
    case 'DATA':
      return 'Data';
    case 'DESIGN':
      return 'Designer';
    case 'ENGINEERING':
      return 'Engineer';
    case 'FINANCE':
      return 'Finance';
    case 'MARKETING':
      return 'Marketer';
    case 'PRODUCT':
      return 'Product';
    case 'SOCIAL_MEDIA':
      return 'Social Media';
    case 'VIDEO':
      return 'Video Editor';
    case 'WRITING':
      return 'Writer';
    case 'OTHER':
    default:
      return 'Freelancer';
  }
};

const POSSIBLE_BADGES = [
  'TOP_INDEPENDENT',
  'PLATFORM_EARNINGS',
  'WALLET_ACTIVE',
  'VERIFICATION',
  'CATEGORY',
  'RATE',
  'MEMBER_SINCE',
  ...EXPERT_PARTNER_USER_GROUPS,
] as const;

export type PossibleBadge = (typeof POSSIBLE_BADGES)[number];

type Props = {
  readonly hiddenBadges?: PossibleBadge[];
  readonly maxCount?: number;
  readonly userProfileRef: null | useBadges_userProfile$key;
};

type Result = {
  badges: Record<PossibleBadge, boolean>;
  categoryBadgeLabel: null | string;
  hasBadges: boolean;
  memberSinceLabel: null | string;
  memberSinceTooltip: null | string;
  platformEarningsAmountLabel: null | string;
  rateLabel: null | string;
};

export const useBadges = ({
  hiddenBadges = ['CATEGORY', 'RATE'],
  maxCount,
  userProfileRef,
}: Props): Result => {
  const data = useFragment<useBadges_userProfile$key>(
    graphql`
      fragment useBadges_userProfile on UserProfile {
        partnerExpertGroups
        showVerificationBadge
        showWalletActiveBadge
        topIndependentStatus
        primaryProfessionalCategory
        platformEarningsBadge

        workPreferences {
          minimumHourlyRate
        }

        firstName
        organizationV2 {
          name
          workspace {
            ... on ClientWorkspace {
              activeSubscription {
                createdAt
              }
            }
          }
        }
      }
    `,
    userProfileRef,
  );

  const { memberSinceLabel, memberSinceTooltip } = useMembersOnlyBadge({
    firstName: data?.firstName,
    subscriptionCreatedAt:
      data?.organizationV2?.workspace?.activeSubscription?.createdAt ?? null,
  });

  if (!data) {
    return {
      badges: POSSIBLE_BADGES.reduce(
        (accumulator, badge) => {
          accumulator[badge] = false;
          return accumulator;
        },
        {} as Record<PossibleBadge, boolean>,
      ),
      categoryBadgeLabel: null,
      hasBadges: false,
      memberSinceLabel: null,
      memberSinceTooltip: null,
      platformEarningsAmountLabel: null,
      rateLabel: null,
    };
  }

  const {
    partnerExpertGroups,
    platformEarningsBadge,
    showVerificationBadge,
    showWalletActiveBadge,
    topIndependentStatus,
    workPreferences,
  } = data;

  const rateLabel = workPreferences.minimumHourlyRate
    ? getRateRangeFromMinHourlyRate(workPreferences.minimumHourlyRate)
    : null;

  const platformEarningsAmount = platformEarningsBadge;

  const badges = POSSIBLE_BADGES.reduce(
    (accumulator, badge) => {
      accumulator[badge] = false;
      return accumulator;
    },
    {} as Record<PossibleBadge, boolean>,
  );

  const shownBadges = POSSIBLE_BADGES.filter((badge) => {
    return hiddenBadges?.includes(badge) === false;
  });

  for (const shownBadge of shownBadges.slice(0, maxCount)) {
    if (partnerExpertGroups.includes(shownBadge as ExpertType)) {
      badges[shownBadge] = true;

      continue;
    }

    switch (shownBadge) {
      case 'CATEGORY': {
        if (data.primaryProfessionalCategory) {
          badges.CATEGORY = true;
        }

        break;
      }

      case 'MEMBER_SINCE': {
        if (memberSinceLabel) {
          badges.MEMBER_SINCE = true;
        }

        break;
      }

      case 'PLATFORM_EARNINGS': {
        if (platformEarningsAmount) {
          badges.PLATFORM_EARNINGS = true;
        }

        break;
      }

      case 'RATE': {
        if (rateLabel) {
          badges.RATE = true;
        }

        break;
      }

      case 'TOP_INDEPENDENT': {
        if (topIndependentStatus === 'OPTED_IN') {
          badges.TOP_INDEPENDENT = true;
        }

        break;
      }

      case 'VERIFICATION': {
        if (showVerificationBadge) {
          badges.VERIFICATION = true;
        }

        break;
      }

      // Note: the wallet active badge is only shown if the platform earnings badge is not shown
      case 'WALLET_ACTIVE': {
        if (showWalletActiveBadge && !platformEarningsAmount) {
          badges.WALLET_ACTIVE = true;
        }

        break;
      }
    }
  }

  return {
    badges,
    categoryBadgeLabel: data.primaryProfessionalCategory
      ? getBadgeFromCategory(data.primaryProfessionalCategory)
      : null,
    hasBadges: Object.values(badges).some(Boolean),
    memberSinceLabel,
    memberSinceTooltip,
    platformEarningsAmountLabel: platformEarningsAmount,
    rateLabel,
  };
};
